import type { EmailMentionableUser } from '../useData';

const SPECIAL_MENTIONS = new Set(['assignee', 'teammates', 'all', 'here', 'team']);
const SPECIAL_MENTION_PATTERN = /^(assignee|teammates|all|here|team)\b/i;

export type MentionSegment =
  | { type: 'text'; text: string }
  | { type: 'mention'; token: string; userId?: number; label: string };

function userNameVariants(user: EmailMentionableUser): string[] {
  const variants = new Set<string>();
  const name = user.name.trim();
  if (name) variants.add(name);
  const local = user.email.split('@')[0]?.trim();
  if (local) variants.add(local);
  return Array.from(variants);
}

function allNameVariants(users: EmailMentionableUser[]): string[] {
  const variants = new Set<string>();
  for (const user of users) {
    for (const name of userNameVariants(user)) {
      variants.add(name);
    }
  }
  return Array.from(variants).sort((a, b) => b.length - a.length);
}

function resolveUserIdForToken(token: string, users: EmailMentionableUser[]): number | undefined {
  if (token.startsWith('user:')) {
    const id = Number.parseInt(token.slice(5), 10);
    if (Number.isFinite(id) && users.some((u) => u.id === id)) return id;
    return undefined;
  }
  if (token.includes('@')) {
    const byEmail = users.find((u) => u.email.toLowerCase() === token.toLowerCase());
    return byEmail?.id;
  }
  const lower = token.toLowerCase();
  for (const user of users) {
    for (const candidate of userNameVariants(user)) {
      const c = candidate.toLowerCase();
      if (c === lower || c.replace(/\s+/g, '') === lower.replace(/\s+/g, '')) {
        return user.id;
      }
    }
  }
  return undefined;
}

export function buildUserMentionInsertText(name: string): string {
  return `@${name.trim()}`;
}

export type MentionBadgeTone = 'person' | 'assignee' | 'teammates' | 'all' | 'here' | 'internal' | 'edit' | 'unknown';

export function mentionBadgeToneForToken(token: string, userId?: number): MentionBadgeTone {
  if (userId != null) return 'person';
  const lower = token.toLowerCase();
  if (lower === 'assignee') return 'assignee';
  if (lower === 'teammates' || lower === 'team') return 'teammates';
  if (lower === 'all') return 'all';
  if (lower === 'here') return 'here';
  return 'unknown';
}

export function mentionBadgeToneForSegment(segment: MentionSegment): MentionBadgeTone {
  if (segment.type === 'text') return 'unknown';
  return mentionBadgeToneForToken(segment.token, segment.userId);
}

export function mentionBadgeClassName(tone: MentionBadgeTone): string {
  switch (tone) {
    case 'person':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200';
    case 'assignee':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200';
    case 'teammates':
      return 'bg-sky-100 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200';
    case 'all':
      return 'bg-violet-100 text-violet-900 dark:bg-violet-950/40 dark:text-violet-200';
    case 'here':
      return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200';
    case 'internal':
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200';
    case 'edit':
      return 'bg-rose-100 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200';
    case 'unknown':
      return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-300';
  }
}

/** Walk @mentions in note text; supports multi-word display names. */
export function parseMentionSegments(body: string, users: EmailMentionableUser[]): MentionSegment[] {
  const segments: MentionSegment[] = [];
  const nameVariants = allNameVariants(users);
  let i = 0;

  while (i < body.length) {
    if (body[i] !== '@') {
      const nextAt = body.indexOf('@', i + 1);
      const end = nextAt === -1 ? body.length : nextAt;
      segments.push({ type: 'text', text: body.slice(i, end) });
      i = end;
      continue;
    }

    const slice = body.slice(i + 1);
    let token: string | null = null;
    let consumed = 0;

    const userIdLegacy = slice.match(/^user:(\d+)/);
    if (userIdLegacy) {
      token = `user:${userIdLegacy[1]}`;
      consumed = userIdLegacy[0].length;
    }

    if (!token) {
      const special = slice.match(SPECIAL_MENTION_PATTERN);
      if (special) {
        token = special[1].toLowerCase();
        consumed = special[1].length;
      }
    }

    if (!token) {
      const email = slice.match(/^([^\s@]+@[^\s@]+)/);
      if (email) {
        token = email[1];
        consumed = email[1].length;
      }
    }

    if (!token) {
      const sliceLower = slice.toLowerCase();
      for (const variant of nameVariants) {
        if (sliceLower.startsWith(variant.toLowerCase())) {
          token = slice.slice(0, variant.length);
          consumed = variant.length;
          break;
        }
      }
    }

    if (!token) {
      const word = slice.match(/^(\S+)/);
      if (word) {
        token = word[1];
        consumed = word[1].length;
      }
    }

    if (!token) {
      segments.push({ type: 'text', text: '@' });
      i += 1;
      continue;
    }

    const userId = resolveUserIdForToken(token, users);
    const label =
      userId != null
        ? users.find((u) => u.id === userId)?.name ?? token
        : SPECIAL_MENTIONS.has(token.toLowerCase())
          ? token.toLowerCase()
          : token;

    segments.push({
      type: 'mention',
      token,
      userId,
      label,
    });
    i += 1 + consumed;
  }

  return segments;
}

export function mentionLabelForToken(token: string, users: EmailMentionableUser[]): string {
  const userId = resolveUserIdForToken(token, users);
  if (userId != null) {
    return users.find((u) => u.id === userId)?.name ?? token;
  }
  if (SPECIAL_MENTIONS.has(token.toLowerCase())) return token.toLowerCase();
  return token;
}

export interface MentionRange {
  start: number;
  end: number;
}

function isAtomicMention(token: string, userId?: number): boolean {
  if (token.startsWith('user:')) return true;
  if (userId != null) return true;
  return SPECIAL_MENTIONS.has(token.toLowerCase());
}

/** Character ranges for completed @mentions that should delete as one unit. */
export function getAtomicMentionRanges(body: string, users: EmailMentionableUser[]): MentionRange[] {
  const ranges: MentionRange[] = [];
  const nameVariants = allNameVariants(users);
  let i = 0;

  while (i < body.length) {
    if (body[i] !== '@') {
      i += 1;
      continue;
    }

    const mentionStart = i;
    const slice = body.slice(i + 1);
    let token: string | null = null;
    let consumed = 0;

    const userIdLegacy = slice.match(/^user:(\d+)/);
    if (userIdLegacy) {
      token = `user:${userIdLegacy[1]}`;
      consumed = userIdLegacy[0].length;
    }

    if (!token) {
      const special = slice.match(SPECIAL_MENTION_PATTERN);
      if (special) {
        token = special[1].toLowerCase();
        consumed = special[1].length;
      }
    }

    if (!token) {
      const email = slice.match(/^([^\s@]+@[^\s@]+)/);
      if (email) {
        token = email[1];
        consumed = email[1].length;
      }
    }

    if (!token) {
      const sliceLower = slice.toLowerCase();
      for (const variant of nameVariants) {
        if (sliceLower.startsWith(variant.toLowerCase())) {
          token = slice.slice(0, variant.length);
          consumed = variant.length;
          break;
        }
      }
    }

    if (!token) {
      i += 1;
      continue;
    }

    const userId = resolveUserIdForToken(token, users);
    if (isAtomicMention(token, userId)) {
      ranges.push({ start: mentionStart, end: mentionStart + 1 + consumed });
    }
    i = mentionStart + 1 + consumed;
  }

  return ranges;
}

export function resolveMentionDeleteRange(
  selStart: number,
  selEnd: number,
  ranges: MentionRange[],
  key: 'Backspace' | 'Delete',
): MentionRange | null {
  if (selStart !== selEnd) {
    let start = selStart;
    let end = selEnd;
    let hit = false;
    for (const range of ranges) {
      if (range.start < end && range.end > start) {
        start = Math.min(start, range.start);
        end = Math.max(end, range.end);
        hit = true;
      }
    }
    return hit ? { start, end } : null;
  }

  const cursor = selStart;
  if (key === 'Backspace') {
    for (const range of ranges) {
      if (cursor > range.start && cursor <= range.end) return range;
    }
    return null;
  }

  for (const range of ranges) {
    if (cursor >= range.start && cursor < range.end) return range;
  }
  return null;
}
